/**
 * @fileoverview Models for receiving data about delivery configurations for use in
 * the Specify Listings Settings Page.
 *
 * This is copied from ListingsStorm2.
 *
 * @author Heesun Jun (hjun@yext.com)
 */

export class DeliveryConfiguration {
  /**
   * @param {{
   *  dontSyncListings: boolean,
   *  dontCreateNewListings: boolean,
   *  automaticallyOptIn: boolean,
   * }} params
   */
  constructor(params) {
    /** @type {boolean} */
    this.dontSyncListings = params.dontSyncListings;
    /** @type {boolean} */
    this.dontCreateNewListings = params.dontCreateNewListings;
    /** @type {boolean} */
    this.automaticallyOptIn = params.automaticallyOptIn;
  }

  /**
   * @param {!Map<number, DeliveryConfiguration>} configurationMap
   * @param {Array<number>} publisherIds
   * @param {boolean} dontSyncListings
   * @return {!Map<number, DeliveryConfiguration>}
   */
  static withDontSyncListings(configurationMap, publisherIds, dontSyncListings) {
    const newConfigurationMap = new Map();
    configurationMap.forEach((value, key) => {
      newConfigurationMap
        .set(key, new DeliveryConfiguration({
          dontSyncListings: (publisherIds.includes(key)) ? dontSyncListings : value.dontSyncListings,
          dontCreateNewListings: value.dontCreateNewListings,
          automaticallyOptIn: value.automaticallyOptIn,
        }));
    });
    return newConfigurationMap;
  }

  /**
   * @param {!Map<number, DeliveryConfiguration>} configurationMap
   * @param {Array<number>} publisherIds
   * @param {boolean} dontCreateNewListings
   * @return {!Map<number, DeliveryConfiguration>}
   */
  static withDontCreateNewListings(configurationMap, publisherIds, dontCreateNewListings) {
    const newConfigurationMap = new Map();
    configurationMap.forEach((value, key) => {
      newConfigurationMap
        .set(key, new DeliveryConfiguration({
          dontSyncListings: value.dontSyncListings,
          dontCreateNewListings: (publisherIds.includes(key)) ? dontCreateNewListings : value.dontCreateNewListings,
          automaticallyOptIn: value.automaticallyOptIn,
        }));
    });
    return newConfigurationMap;
  }

  /**
   * @param {!Map<number, DeliveryConfiguration>} configurationMap
   * @param {Array<number>} publisherIds
   * @param {boolean} automaticallyOptIn
   * @return {!Map<number, DeliveryConfiguration>}
   */
  static withAutomaticallyOptIn(configurationMap, publisherIds, automaticallyOptIn) {
    const newConfigurationMap = new Map();
    configurationMap.forEach((value, key) => {
      newConfigurationMap
        .set(key, new DeliveryConfiguration({
          dontSyncListings: value.dontSyncListings,
          dontCreateNewListings: value.dontCreateNewListings,
          automaticallyOptIn: (publisherIds.includes(key)) ? automaticallyOptIn : value.automaticallyOptIn,
        }));
    });
    return newConfigurationMap;
  }

  /**
   * @param {!Array<DeliveryConfigurationJson>} array
   * @return {!Map<number, DeliveryConfiguration>}
   */
  static buildMapFromJSON(array) {
    return new Map(array
      .map(json => [json['publisherId'],
        new DeliveryConfiguration({
          dontSyncListings: json['dontSyncListings'],
          dontCreateNewListings: json['dontCreateNewListings'],
          automaticallyOptIn: json['automaticallyOptIn'],
        })],
      ));
  }

  /**
   * @param {!Map<number, DeliveryConfiguration>} deliveryConfigurationMap
   * @return {!Array<Object>}
   */
  static toJSONFromMap(deliveryConfigurationMap) {
    const jsonArray = [];
    if (deliveryConfigurationMap !== undefined) {
      deliveryConfigurationMap.forEach((value, key) => {
        jsonArray.push({
          'publisherId': key,
          'dontSyncListings': value.dontSyncListings,
          'dontCreateNewListings': value.dontCreateNewListings,
          'automaticallyOptIn': value.automaticallyOptIn,
        });
      });
    }
    return jsonArray;
  }
}

/**
 * @dict
 * @typedef {{
 *  publisherId: number,
 *  dontSyncListings: boolean,
 *  dontCreateNewListings: boolean,
 * }}
 */
export var DeliveryConfigurationJson;
